import React, { useMemo, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import type { CheckboxGroupProps, MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  Select,
  CtaBtn,
  MultiSelect,
  SecondaryBtn,
  StickyFooter,
  CheckboxGroup,
  Checkbox,
} from '@tmapy/style-guide';

import type { DmxConfig, OmittedFormItemProps } from '../../types';
import type { InputProps } from '../../hooks/useInput';
import {
  useAppContext,
  useCheckbox,
  useCheckboxGroup,
  useInput,
  useMultiSelect,
  useSelector,
} from '../../hooks';
import { APP_TYPE } from '../../constants';

import type { CadasterListResponse } from '../../openapi/types';
import type { DocsListQuery, DocsListResponse } from '../../openapi/operations/docsList';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';
import { UtilitiesTypeEnum } from '../../openapi/types/UtilitiesTypeEnum';
import { cadastersList } from '../../openapi/operations/cadastersList';
import { PrejimkaDmvsSourceEnum } from '../../openapi/types/PrejimkaDmvsSourceEnum';
import { setDocListFilter } from '../../redux/filter/actions';
import { setMenuRouteFilterId } from '../../redux/menu/actions';

import {
  DOCUMENTATION_TYPES,
  DOC_STATES_MANAGER,
  DOC_STATES_PREJIMKA,
  DATA_ORIGIN_TYPES,
  DOC_STATE,
  usableFilterItemsForAppType,
} from '../constants';

import {
  documentationTypesMessages,
  docFormMessages,
  docStateMessages,
  dataOriginTypesMessages,
  docFilterMessages,
  docUtilitiesTypeMessages,
  prejimkaTypesMessages,
  docInternalInfoMessages,
} from './docsMessages';
import { useCanManageDocInternalInfo } from './DocInternalInfo';

const renderItemMultiSelect = (item: { name: string }) => item.name;

const getIdFromObject = (obj?: { id?: string | number }) => {
  if (!obj) return null;
  return obj?.id ?? null;
};

const messagesParams = { id: null };

type DocFiltersProps = {
  filters: DocsListQuery;
  customerType: DmxConfig['customerType'];
  dispatch: Dispatch;
};

type CheckboxGroupFilter = Omit<CheckboxGroupProps<any>, 'onChange'> & {
  onChange: (e: React.ChangeEvent<HTMLInputElement>, eventData?: { values: any[] }) => void;
};

type PrejimkaTypeItemType = {
  name?: string;
  label: string;
  value: Partial<PrejimkaTypesEnum> | undefined;
};

export const DocFilter: React.FC<DocFiltersProps> = ({ filters, customerType, dispatch }) => {
  const app = useAppContext();
  const formatMessage = useMessage();
  const { manager } = useSelector((state) => state.app.config);
  const [checkboxGroupForceRefreshKey, setCheckboxGroupForceRefreshKey] = useState(0);
  const [prejimkaTypeChecked, setPrejimkaTypeChecked] = useState<{
    [ident: string]: boolean;
  } | null>(null);
  const [prejimkaTypeValues, setPrejimkaTypeValues] = useState<PrejimkaTypeItemType[]>([]);

  const usableItems = usableFilterItemsForAppType[customerType];

  // TODO fromassigned_number to ID
  const id = useInput<InputProps, number>({
    name: 'id',
    initialValue: filters.id,
    label: formatMessage(docFormMessages.id),
  });

  const geometry_plan_number = useInput<InputProps>({
    name: 'geometry_plan_number',
    initialValue: filters.geometry_plan_number as string,
    label: formatMessage(docFormMessages.geometry_plan_number),
  });

  const utilitiesTypeItems = useSelectMessages(docUtilitiesTypeMessages, UtilitiesTypeEnum);
  const utilities_type = useInput({
    name: 'utilities_type__id__in',
    initialValue: filters.utilities_type,
    items: utilitiesTypeItems,
    label: formatMessage(docFormMessages.utilities_type),
  });

  const [allCadasters, setAllCadasters] = useState<CadasterListResponse[] | undefined>();
  const prepareCadastersItems = useCallback(async (val: string) => {
    const response = await cadastersList({ query: { name__icontains: val } });
    if (response.data) {
      if (!allCadasters) {
        setAllCadasters(response.data.results);
      }
      return response.data.results;
    }
    return null;
  }, []);
  const cadastersInitialValues = useMemo(() => {
    if (allCadasters && filters.cadasters) {
      return allCadasters.filter(({ id }) => filters.cadasters!.includes(id));
    }

    return [];
  }, [filters, allCadasters]);
  const cadasters = useMultiSelect<
    MultiSelectProps<CadasterListResponse> | OmittedFormItemProps,
    CadasterListResponse
  >({
    name: 'cadasters',
    label: formatMessage(docFormMessages.cadasters),
    initialValue: cadastersInitialValues,
    prepareItems: prepareCadastersItems,
    renderValue: (item) => (item.name ? item.name : ''),
    renderItem: renderItemMultiSelect,
  });

  const name = useInput<InputProps>({
    name: 'name__icontains',
    initialValue: filters.name__icontains,
    label: formatMessage(docFormMessages.name),
  });

  const description = useInput<InputProps>({
    name: 'description__icontains',
    initialValue: filters.description__icontains,
    label: formatMessage(docFormMessages.description),
  });

  const owner_name = useInput<InputProps>({
    name: 'owner_name__icontains',
    initialValue: filters.owner_name__icontains,
    label: formatMessage(docFormMessages.owner),
  });

  const owner_ico = useInput<InputProps>({
    name: 'owner_ico__icontains',
    initialValue: filters.owner_ico__icontains,
    label: formatMessage(docFormMessages.owner_ico),
  });

  const owner_birth_number = useInput<InputProps>({
    name: 'owner_birth_number__icontains',
    initialValue: filters.owner_birth_number__icontains,
    label: formatMessage(docFormMessages.owner_birth_number),
  });

  const [createdGte, setCreateGt] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGt('');
    },
  };

  const handleCreateGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGt(eventData.value);
  };

  const [createdLt, setCreateLt] = useState(filters.created__lt);
  const created__lt = {
    name: 'created__lt',
    clear: () => {
      setCreateLt('');
    },
  };

  const handleCreateLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLt(eventData.value);
  };

  // FINISHED__GTE
  const [finishedGte, setFinishedGte] = useState(filters.finished__gte);
  const finished__gte = {
    name: 'finished__gte',
    clear: () => {
      setFinishedGte('');
    },
  };
  const handleFinishedGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedGte(eventData.value);
  };
  // FINISHED__LTE
  const [finishedLte, setFinishedLte] = useState(filters.finished__lte);
  const finished__lte = {
    name: 'finished__lte',
    clear: () => {
      setFinishedLte('');
    },
  };
  const handleFinishedLteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setFinishedLte(eventData.value);
  };

  // CLOSED__GTE
  const [closedGte, setClosedGte] = useState(filters.closed__gte ?? '');
  const closed__gte = {
    name: 'closed__gte',
    clear: () => {
      setClosedGte('');
    },
  };
  const handleClosedGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setClosedGte(eventData.value);
  };
  // CLOSED__LT
  const [closedLt, setClosedLt] = useState(filters.closed__lt ?? '');
  const closed__lt = {
    name: 'closed__lt',
    clear: () => {
      setClosedLt('');
    },
  };
  const handleClosedLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setClosedLt(eventData.value);
  };

  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani__icontains',
    initialValue: filters.dmvs_id_podani__icontains,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });

  const creatorFullName = useInput<InputProps>({
    name: 'creator_full_name',
    initialValue: filters.creator_full_name,
    label: formatMessage(docFormMessages.creatorFullName),
  });

  const receiverFullName = useInput<InputProps>({
    name: 'receiver_full_name',
    initialValue: filters.receiver_full_name,
    label: formatMessage(docFormMessages.receiverFullName),
  });

  const geodesistFullName = useInput<InputProps>({
    name: 'geodesist_full_name',
    initialValue: filters.geodesist_full_name,
    label: formatMessage(docFormMessages.geodesist),
  });

  const processor_name = useInput<InputProps>({
    name: 'processor_name__icontains',
    initialValue: filters.processor_name__icontains,
    label: formatMessage(docFormMessages.processor),
  });

  const processor_ico = useInput<InputProps>({
    name: 'processor_ico__icontains',
    initialValue: filters.processor_ico__icontains,
    label: formatMessage(docFormMessages.processor_ico),
  });

  const processor_birth_number = useInput<InputProps>({
    name: 'processor_birth_number__icontains',
    initialValue: filters.processor_birth_number__icontains,
    label: formatMessage(docFormMessages.processor_birth_number),
  });

  const editorManagerFullName = useInput<InputProps>({
    name: 'editor_manager_full_name',
    initialValue: filters.editor_manager_full_name,
    label: formatMessage(docFormMessages.editor_manager),
  });

  const original_id__isnull = useCheckbox<InputProps>({
    name: 'original_id__isnull',
    initialValue:
      filters.original_id__isnull || filters.original_id__isnull === undefined ? false : true,
    label: formatMessage(docFormMessages.documentation_type),
  });

  const docStates = app.appType === APP_TYPE.PREJIMKA ? DOC_STATES_PREJIMKA : DOC_STATES_MANAGER;

  const stateItems = useSelectMessages(docStateMessages, docStates as any, messagesParams);

  const prepareStateItems = useCallback(
    (val: DOC_STATE) => {
      return stateItems.filter(
        (state) => unaccentAndLower(state.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [stateItems],
  );
  const stateInitialValues = useMemo(() => {
    return filters.state__in?.length
      ? stateItems.filter((state) => filters.state__in!.includes(state.value))
      : [];
  }, [stateItems, filters.state__in]);

  const state__in = useMultiSelect<
    MultiSelectProps<DocsListResponse> | OmittedFormItemProps,
    (typeof stateItems)[0]
  >({
    name: 'state__in',
    label: formatMessage(docFormMessages.state),
    initialValue: stateInitialValues,
    prepareItems: prepareStateItems,
    renderValue: (state: (typeof stateItems)[0]) => state.label,
    renderItem: (state: (typeof stateItems)[0]) => state.label,
  });

  const documentationTypeItems = useSelectMessages(documentationTypesMessages, DOCUMENTATION_TYPES);

  // TODO <SelectProps | FormItemProps>
  const documentation_type = useInput<{ items: any; label: string }, any>({
    name: 'documentation_type',
    initialValue: filters.documentation_type,
    items: documentationTypeItems,
    label: formatMessage(docFormMessages.documentation_type),
  });

  const dataOriginItems = useSelectMessages(dataOriginTypesMessages, DATA_ORIGIN_TYPES);

  // TODO <SelectProps | FormItemProps>
  const data_origin = useInput<{ items: any; label: string }, any>({
    name: 'data_origin',
    initialValue: filters.data_origin,
    items: dataOriginItems,
    label: formatMessage(docFormMessages.data_origin),
  });

  // Use only specific types
  const includedItems: Partial<PrejimkaTypesEnum>[] = [
    PrejimkaTypesEnum.DTM,
    PrejimkaTypesEnum.DMVS_DTI,
    PrejimkaTypesEnum.DMVS_GAD,
  ];
  if (manager?.modules?.podaniKad) {
    includedItems.push(PrejimkaTypesEnum.DTM_KAD);
  }

  const prejimkaTypeItems = useSelectMessages(prejimkaTypesMessages, PrejimkaTypesEnum).filter(
    (item) => includedItems.includes(item.value),
  );
  const onPrejimkaTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, data: { values: PrejimkaTypeItemType[] }) => {
      const checkbox = e.target;
      setPrejimkaTypeChecked({ ...prejimkaTypeChecked, [checkbox.name]: checkbox.checked });
      setPrejimkaTypeValues(data.values);
    },
    [prejimkaTypeChecked],
  );

  const prejimkaTypeInitialValue = useMemo(() => {
    return prejimkaTypeValues.length
      ? prejimkaTypeValues
      : filters.prejimka_type__in?.map((item) => {
          return { value: item, label: item } as PrejimkaTypeItemType;
        }) || [];
  }, [prejimkaTypeValues, filters.prejimka_type__in]);

  const prejimka_type = useCheckboxGroup({
    name: 'prejimka_type',
    items: prejimkaTypeItems as PrejimkaTypeItemType[],
    initialValue: prejimkaTypeInitialValue,
    renderLabel: (item: PrejimkaTypeItemType) => (
      <span className='sg-u-type-sml'>{item.label}</span>
    ),
    renderValue: (item: PrejimkaTypeItemType) => `${item.value}`,
    onChange: onPrejimkaTypeChange,
    label: formatMessage(docFormMessages.prejimka_type),
  }) as CheckboxGroupFilter;

  const buildingCategoryItems = useMemo(() => {
    return [
      {
        label: formatMessage(docFormMessages.has_planimetry),
        value: 'has_planimetry',
      },
      {
        label: formatMessage(docFormMessages.has_utilities),
        value: 'has_utilities',
      },
    ];
  }, [formatMessage]);

  const buildingCategory = useInput({
    name: 'buildingCategory',
    initialValue: filters.has_planimetry
      ? 'has_planimetry'
      : filters.has_utilities
        ? 'has_utilities'
        : undefined,
    items: buildingCategoryItems,
    label: formatMessage(docFormMessages.buildingCategory),
  });

  // DMVS_SOURCE
  const dmvsSourceItems = useSelectMessages({} as Record<string, string>, PrejimkaDmvsSourceEnum);
  const dmvs_source = useInput({
    name: 'dmvs_source',
    initialValue: filters.dmvs_source,
    items: dmvsSourceItems,
    label: formatMessage(docFormMessages.dmvs_source),
  });

  const responsiblePersonFullName = useInput<InputProps>({
    name: 'responsible_person_full_name',
    initialValue: filters.responsible_person_full_name,
    label: formatMessage(docInternalInfoMessages.responsiblePerson),
  });
  const canManageInternalInfo = useCanManageDocInternalInfo();

  const filterClearScope = [
    name,
    description,
    state__in,
    id,
    documentation_type,
    geometry_plan_number,
    buildingCategory,
    utilities_type,
    cadasters,
    data_origin,
    created__gte,
    created__lt,
    finished__gte,
    finished__lte,
    closed__gte,
    closed__lt,
    creatorFullName,
    receiverFullName,
    owner_name,
    owner_ico,
    owner_birth_number,
    processor_name,
    processor_ico,
    processor_birth_number,
    geodesistFullName,
    editorManagerFullName,
    dmvs_id_podani,
    dmvs_source,
    original_id__isnull,
    responsiblePersonFullName,
  ];

  const handleClearBtnClick = useCallback(() => {
    filterClearScope.forEach((formItem) => formItem?.clear?.());

    // clear/forceRefresh - checkboxGroup - prejimka_type
    setPrejimkaTypeChecked(null);
    setPrejimkaTypeValues([]);
    setCheckboxGroupForceRefreshKey(checkboxGroupForceRefreshKey + 1);

    dispatch(setDocListFilter({}));
    // reset filters - reset/set menu item
    dispatch(setMenuRouteFilterId('managerDocListAll'));
  }, [checkboxGroupForceRefreshKey]);

  function handleSubmit() {
    const prejimka_type__in = prejimkaTypeChecked
      ? (Object.keys(prejimkaTypeChecked).filter(
          (key) => prejimkaTypeChecked[key],
        ) as PrejimkaTypesEnum[])
      : undefined;

    dispatch(
      setDocListFilter({
        name__icontains: name.value,
        description__icontains: description.value,
        state__in: state__in.values.map((state) => state.value),
        id: id.value,
        documentation_type: documentation_type.value,
        geometry_plan_number: geometry_plan_number.value,
        has_planimetry: buildingCategory.value === 'has_planimetry' ? true : undefined,
        has_utilities: buildingCategory.value === 'has_utilities' ? true : undefined,
        utilities_type: utilities_type.value,
        cadasters: cadasters.values.length
          ? (cadasters.values.map(getIdFromObject) as number[])
          : undefined,
        data_origin: data_origin.value,
        prejimka_type__in,
        created__gte: createdGte,
        created__lt: createdLt,
        finished__gte: finishedGte,
        finished__lte: finishedLte,
        closed__gte: closedGte,
        closed__lt: closedLt,
        creator_full_name: creatorFullName.value,
        receiver_full_name: receiverFullName.value,
        owner_name__icontains: owner_name.value,
        owner_ico__icontains: owner_ico.value,
        owner_birth_number__icontains: owner_birth_number.value,
        processor_name__icontains: processor_name.value,
        processor_ico__icontains: processor_ico.value,
        processor_birth_number__icontains: processor_birth_number.value,
        geodesist_full_name: geodesistFullName.value,
        editor_manager_full_name: editorManagerFullName.value,
        responsible_person_full_name: responsiblePersonFullName.value,
        dmvs_id_podani__icontains: dmvs_id_podani.value,
        dmvs_source: dmvs_source.value,
        original_id__isnull: original_id__isnull.isChecked ? false : undefined,
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        {(usableItems.includes('name') || usableItems.includes('description')) && (
          <FormRow>
            {usableItems.includes('name') && (
              <FormItem {...name}>
                <Input {...name} />
              </FormItem>
            )}
            {usableItems.includes('description') && (
              <FormItem {...description}>
                <Input {...description} />
              </FormItem>
            )}
          </FormRow>
        )}
        {(usableItems.includes('state') || usableItems.includes('id')) && (
          <FormRow>
            {usableItems.includes('state') && (
              <FormItem {...state__in}>
                <MultiSelect {...state__in} />
              </FormItem>
            )}
            {usableItems.includes('id') && (
              <FormItem {...id}>
                <Input {...id} />
              </FormItem>
            )}
          </FormRow>
        )}
        {app.appType === APP_TYPE.PREJIMKA && (
          <FormRow>
            <FormItem {...original_id__isnull}>
              <Checkbox {...original_id__isnull}>
                <FormattedMessage {...docFormMessages.original_id__isnull} />
              </Checkbox>
            </FormItem>
          </FormRow>
        )}

        {(usableItems.includes('documentation_type') || usableItems.includes('prejimka_type')) && (
          <FormRow>
            {usableItems.includes('documentation_type') && (
              <FormItem {...documentation_type}>
                <Select hasEmptyItem {...documentation_type} />
              </FormItem>
            )}
            {usableItems.includes('prejimka_type') && (
              <FormItem {...prejimka_type}>
                <CheckboxGroup
                  key={checkboxGroupForceRefreshKey}
                  {...(prejimka_type as CheckboxGroupFilter)}
                  direction='row'
                />
              </FormItem>
            )}
          </FormRow>
        )}
        {(usableItems.includes('geometry_plan_number') ||
          usableItems.includes('buildingCategory')) && (
          <FormRow>
            {usableItems.includes('geometry_plan_number') && (
              <FormItem {...geometry_plan_number}>
                <Input {...geometry_plan_number} />
              </FormItem>
            )}
            {usableItems.includes('buildingCategory') && (
              <FormItem {...buildingCategory}>
                <Select hasEmptyItem {...buildingCategory} />
              </FormItem>
            )}
          </FormRow>
        )}
        {usableItems.includes('utilities_type') && (
          <FormRow>
            <FormItem {...utilities_type}>
              <Select hasEmptyItem {...utilities_type} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('cadasters') && (
          <FormRow>
            <FormItem {...cadasters}>
              <MultiSelect {...cadasters} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('data_origin') && (
          <FormRow>
            <FormItem {...data_origin}>
              <Select hasEmptyItem {...data_origin} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('owner') && (
          <div className='sg-u-bt sg-a-pt-2 sg-u-vs-2'>
            <FormRow>
              <FormItem {...owner_name}>
                <Input {...owner_name} />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem {...owner_ico}>
                <Input {...owner_ico} />
              </FormItem>
              <FormItem {...owner_birth_number}>
                <Input {...owner_birth_number} />
              </FormItem>
            </FormRow>
          </div>
        )}
        {usableItems.includes('processor') && (
          <div className='sg-u-bt sg-u-bb sg-a-pt-2 sg-a-pb-2 sg-u-vs-2'>
            <FormRow>
              <FormItem {...processor_name}>
                <Input {...processor_name} />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem {...processor_ico}>
                <Input {...processor_ico} />
              </FormItem>
              <FormItem {...processor_birth_number}>
                <Input {...processor_birth_number} />
              </FormItem>
            </FormRow>
          </div>
        )}
        {usableItems.includes('created') && (
          <FormRow>
            <FormItem label={formatMessage(docFormMessages.createdGte)}>
              <Input
                type='date'
                name='created__gte'
                value={createdGte}
                onChange={handleCreateGtChange}
              />
            </FormItem>
            <FormItem label={formatMessage(docFormMessages.createdLt)}>
              <Input
                type='date'
                name='created__lt'
                value={createdLt}
                onChange={handleCreateLtChange}
              />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('finished') && (
          <FormRow>
            <FormItem label={formatMessage(docFormMessages.finishedGte)}>
              <Input
                type='date'
                name='finished__gte'
                value={finishedGte}
                onChange={handleFinishedGteChange}
              />
            </FormItem>
            <FormItem label={formatMessage(docFormMessages.finishedLte)}>
              <Input
                type='date'
                name='finished__lte'
                value={finishedLte}
                onChange={handleFinishedLteChange}
              />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('closed') && (
          <FormRow>
            <FormItem label={formatMessage(docFormMessages.closedGte)}>
              <Input
                type='date'
                name='closed__gte'
                value={closedGte}
                onChange={handleClosedGteChange}
              />
            </FormItem>
            <FormItem label={formatMessage(docFormMessages.closedLt)}>
              <Input
                type='date'
                name='closed__lt'
                value={closedLt}
                onChange={handleClosedLtChange}
              />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('dmvs_id_podani') && (
          <FormRow>
            <FormItem {...dmvs_id_podani}>
              <Input {...dmvs_id_podani} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('dmvs_source') && (
          <FormRow>
            <FormItem {...dmvs_source}>
              <Select hasEmptyItem {...dmvs_source} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('creatorFullName') && (
          <FormRow>
            <FormItem {...creatorFullName}>
              <Input {...creatorFullName} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('receiverFullName') && (
          <FormRow>
            <FormItem {...receiverFullName}>
              <Input {...receiverFullName} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('geodesistFullName') && (
          <FormRow>
            <FormItem {...geodesistFullName}>
              <Input {...geodesistFullName} />
            </FormItem>
          </FormRow>
        )}
        {usableItems.includes('editorManagerFullName') && (
          <FormRow>
            <FormItem {...editorManagerFullName}>
              <Input {...editorManagerFullName} />
            </FormItem>
          </FormRow>
        )}
        {canManageInternalInfo && (
          <FormRow>
            <FormItem {...responsiblePersonFullName}>
              <Input {...responsiblePersonFullName} />
            </FormItem>
          </FormRow>
        )}
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...docFilterMessages.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...docFilterMessages.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
